import React, { useState } from 'react';
import { 
  ChevronUpIcon, 
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';

const GenericDataTable = ({ data, columns, pageSize = 10, onExport }) => {
  const [sortField, setSortField] = useState(columns[0]?.key || '');
  const [sortDirection, setSortDirection] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pageSize);

  // Ordenação
  const sortedData = [...data].sort((a, b) => {
    let aValue = a[sortField];
    let bValue = b[sortField];

    // Encontrar o tipo da coluna
    const column = columns.find(col => col.key === sortField);
    const columnType = column?.type || 'string';

    // Tratamento por tipo
    if (columnType === 'number') {
      aValue = parseFloat(aValue) || 0;
      bValue = parseFloat(bValue) || 0;
    } else if (columnType === 'date') {
      aValue = new Date(aValue || 0);
      bValue = new Date(bValue || 0);
    } else {
      // Tratamento para strings
      aValue = (aValue || '').toString().toLowerCase();
      bValue = (bValue || '').toString().toLowerCase();
    }

    if (sortDirection === 'asc') {
      return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
    } else {
      return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
    }
  });

  // Paginação
  const totalPages = Math.ceil(sortedData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentData = sortedData.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return null;
    return sortDirection === 'asc' ? 
      <ChevronUpIcon className="w-4 h-4" /> : 
      <ChevronDownIcon className="w-4 h-4" />;
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const formatCellValue = (value, column) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    }

    if (column.type === 'number') {
      return new Intl.NumberFormat('pt-BR').format(value);
    }

    if (column.type === 'currency') {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL'
      }).format(value);
    }

    return value.toString();
  };

  return (
    <div className="card-dark">
      {/* Header com informações e exportação */}
      <div className="p-4 border-b border-gray-700 flex justify-between items-center">
        <div className="text-dark-text-secondary">
          Mostrando {startIndex + 1}-{Math.min(endIndex, sortedData.length)} de {sortedData.length} itens
        </div>
        <div className="flex items-center gap-4">
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="input-dark text-sm"
          >
            <option value={5}>5 por página</option>
            <option value={10}>10 por página</option>
            <option value={25}>25 por página</option>
            <option value={50}>50 por página</option>
          </select>
          {onExport && (
            <button
              onClick={onExport}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <DocumentArrowDownIcon className="w-4 h-4" />
              Exportar
            </button>
          )}
        </div>
      </div>

      {/* Tabela */}
      <div className="overflow-x-auto">
        <table className="table-dark">
          <thead>
            <tr>
              {columns.map((column) => (
                <th 
                  key={column.key}
                  className={`${column.sortable ? 'cursor-pointer hover:bg-gray-600 select-none' : ''}`}
                  onClick={column.sortable ? () => handleSort(column.key) : undefined}
                >
                  <div className="flex items-center gap-2">
                    {column.label}
                    {column.sortable && getSortIcon(column.key)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {currentData.map((item, index) => (
              <tr key={index}>
                {columns.map((column) => (
                  <td 
                    key={column.key}
                    className={`${column.key === 'codbar' ? 'font-mono text-sm' : ''} ${
                      column.key === 'description' ? 'max-w-xs truncate' : ''
                    } ${column.type === 'number' ? 'text-right' : ''}`}
                    title={column.key === 'description' ? item[column.key] : undefined}
                  >
                    {formatCellValue(item[column.key], column)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mensagem quando não há dados */}
      {currentData.length === 0 && (
        <div className="p-8 text-center text-dark-text-secondary">
          <p>Nenhum item encontrado</p>
        </div>
      )}

      {/* Paginação */}
      {totalPages > 1 && (
        <div className="p-4 border-t border-gray-700 flex justify-between items-center">
          <div className="text-dark-text-secondary text-sm">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2"
            >
              <ChevronLeftIcon className="w-4 h-4" />
            </button>
            
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (currentPage <= 3) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`px-3 py-1 rounded text-sm transition-colors ${
                    pageNum === currentPage
                      ? 'bg-primary text-white'
                      : 'bg-gray-700 text-dark-text-secondary hover:bg-gray-600'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed p-2"
            >
              <ChevronRightIcon className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GenericDataTable;
